// src/RequirementsOverview.tsx
import React, { useState, useEffect } from 'react';
import { VISA_REQUIREMENTS_DATA, DestinationRequirement } from './data/visaRequirements';
import { loadAdminConfig, loadAdminConfigAsync, AdminStoreConfig, getGuidelineTabsForCountry } from './data/adminStore';
import { safeGetItem } from './data/storage';

interface RequirementsOverviewProps {
  countryCode: 'INDIA' | 'MALAYSIA' | 'VIETNAM' | string;
  onStartApplication: () => void;
}

export const RequirementsOverview: React.FC<RequirementsOverviewProps> = ({
  countryCode,
  onStartApplication,
}) => {
  const [adminConfig, setAdminConfig] = useState<AdminStoreConfig>(loadAdminConfig);
  const [activeCategoryTab, setActiveCategoryTab] = useState<string>('TOURIST');
  const [isAgentUnlocked, setIsAgentUnlocked] = useState<boolean>(
    () => safeGetItem('visaAgentUnlocked') === 'true'
  );

  useEffect(() => {
    loadAdminConfigAsync().then((conf) => {
      setAdminConfig(conf);
    });

    const handleSync = () => {
      setAdminConfig(loadAdminConfig());
      setIsAgentUnlocked(safeGetItem('visaAgentUnlocked') === 'true');
    };
    window.addEventListener('adminConfigUpdated', handleSync);
    window.addEventListener('agentStatusChanged', handleSync);
    return () => {
      window.removeEventListener('adminConfigUpdated', handleSync);
      window.removeEventListener('agentStatusChanged', handleSync);
    };
  }, []);

  const customReq = adminConfig.customRequirements?.[countryCode.toUpperCase()];
  const reqData: DestinationRequirement =
    customReq || VISA_REQUIREMENTS_DATA[countryCode.toUpperCase()] || VISA_REQUIREMENTS_DATA.INDIA;

  const rate = adminConfig.exchangeRateLkr || 340;
  const hideUsd = adminConfig.hideUsdFromPublic;

  // Render configured tabs for THIS specific country (filter hidden for public)
  const configuredTabs = getGuidelineTabsForCountry(adminConfig, countryCode);
  const visibleTabs = configuredTabs.filter((t) => !t.isHidden || isAgentUnlocked);

  const visiblePrices = reqData.visaPrices.filter((p) => !p.isHidden || isAgentUnlocked);

  // Filter prices strictly by active category tab
  const categoryFilteredPrices = visiblePrices.filter((p) => {
    const nameLower = p.name.toLowerCase();
    if (activeCategoryTab === 'BUSINESS') {
      return nameLower.includes('business');
    }
    if (activeCategoryTab === 'MEDICAL') {
      return nameLower.includes('medical') || nameLower.includes('ayush');
    }
    if (activeCategoryTab === 'CONFERENCE') {
      return nameLower.includes('conference');
    }
    // TOURIST or fallback
    return nameLower.includes('tourist') || (!nameLower.includes('business') && !nameLower.includes('medical') && !nameLower.includes('ayush') && !nameLower.includes('conference'));
  });

  // Dynamic Category Specific Checklists (from reqData.categoryDocuments if set)
  const getCategoryDocuments = () => {
    const defaultReq = VISA_REQUIREMENTS_DATA[countryCode.toUpperCase()];
    if (reqData.categoryDocuments && reqData.categoryDocuments[activeCategoryTab] && reqData.categoryDocuments[activeCategoryTab].length > 0) {
      return reqData.categoryDocuments[activeCategoryTab];
    }
    if (defaultReq && defaultReq.categoryDocuments && defaultReq.categoryDocuments[activeCategoryTab]) {
      return defaultReq.categoryDocuments[activeCategoryTab];
    }
    return reqData.requiredDocuments;
  };

  const getCategoryRules = () => {
    if (activeCategoryTab === 'BUSINESS') {
      return [
        'e-Business Visa is valid for 1 Year with Multiple Entries',
        'Max continuous stay is 180 days per visit',
        'Must carry Business Card & Indian Host Company invitation letter',
      ];
    }
    if (activeCategoryTab === 'MEDICAL') {
      return [
        'e-Medical Visa is valid for 60 Days with Triple Entries',
        'Up to 2 e-Medical Attendant visas allowed per patient',
        'Must present Hospital recommendation letter on arrival',
      ];
    }
    return reqData.entryRequirements;
  };

  return (
    <div className="card-container animate-fade-in" style={{ marginBottom: '2rem' }}>
      {/* HEADER TITLE */}
      <div
        style={{
          borderBottom: '2px solid #e2e8f0',
          paddingBottom: '1rem',
          marginBottom: '1.25rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span
              style={{
                background: '#eff6ff',
                color: '#1d4ed8',
                padding: '0.35rem 0.85rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {reqData.flag} {reqData.countryName} e-Visa Guidelines
            </span>

            {(() => {
              const currentHelper = adminConfig.helpers?.find(
                (h) => h.country.toLowerCase() === reqData.countryName.toLowerCase() || h.id.toLowerCase() === countryCode.toLowerCase()
              );
              const processingTimeText = currentHelper?.processingTime || '3 - 4 Business Days';
              return (
                <span
                  style={{
                    background: '#fef3c7',
                    color: '#b45309',
                    border: '1px solid #fde68a',
                    padding: '0.35rem 0.85rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                  }}
                >
                  ⏱️ Processing: {processingTimeText}
                </span>
              );
            })()}
          </div>
          <h3 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#0f172a', marginTop: '0.4rem', margin: 0 }}>
            Document Checklist, Photo Specs & Visa Fee Schedule
          </h3>
        </div>

        {isAgentUnlocked && (
          <span
            style={{
              background: '#f0fdf4',
              color: '#166534',
              border: '1px solid #bbf7d0',
              padding: '0.35rem 0.85rem',
              borderRadius: '9999px',
              fontSize: '0.75rem',
              fontWeight: '700',
            }}
          >
            🟢 Agent Mode: Prices Include Profit Markup
          </span>
        )}
      </div>

      {/* CATEGORY SELECTOR TABS */}
      {visibleTabs.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>
          {visibleTabs.map((tab) => {
            const isActive = tab.id === activeCategoryTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveCategoryTab(tab.id)}
                style={{
                  padding: '0.65rem 1.25rem',
                  borderRadius: '10px',
                  border: isActive ? '2px solid #2563eb' : '1px solid #cbd5e1',
                  background: isActive ? '#eff6ff' : '#ffffff',
                  color: isActive ? '#1e3a8a' : '#475569',
                  fontWeight: isActive ? '800' : '600',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  boxShadow: isActive ? '0 4px 12px rgba(37, 99, 235, 0.15)' : 'none',
                  transition: 'all 0.15s ease',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* 1. VISA PRICES FOR SELECTED CATEGORY */}
      {categoryFilteredPrices.length > 0 && (
        <div style={{ marginBottom: '1.75rem' }}>
          <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#1e3a8a', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span>💳</span> Estimated Total Fee Breakdown ({activeCategoryTab})
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
            {categoryFilteredPrices.map((price, idx) => {
              const govtUsd = price.govtUsd;
              const markupUsd = price.agentMarkupUsd;
              const totalUsd = govtUsd + markupUsd;
              const totalLkr = Math.round(totalUsd * rate);

              return (
                <div
                  key={idx}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '1rem 1.25rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                    position: 'relative',
                  }}
                >
                  <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#0f172a' }}>{price.name}</div>

                  {/* PRICE DISPLAY */}
                  <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#15803d', margin: '0.35rem 0' }}>
                    LKR {totalLkr.toLocaleString()} Rs
                    {!hideUsd && (
                      <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', marginLeft: '0.5rem' }}>
                        (${totalUsd} USD)
                      </span>
                    )}
                  </div>

                  {/* AGENT MODE PROFIT BREAKDOWN */}
                  {isAgentUnlocked && (
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '0.35rem 0.6rem', fontSize: '0.75rem', color: '#166534', fontWeight: '700', marginBottom: '0.35rem' }}>
                      🟢 Agent Breakdown: Govt ${govtUsd} + Profit ${markupUsd} = Total ${totalUsd}
                    </div>
                  )}

                  <div style={{ fontSize: '0.775rem', color: '#64748b' }}>{price.description}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 2. TWO COLUMN LAYOUT: PHOTO SPECS & DYNAMIC DOC CHECKLIST */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.25rem', marginBottom: '1.75rem' }}>
        {/* Photo Specs */}
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.25rem' }}>
          <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#1e3a8a', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span>📸</span> Photo Specifications ({activeCategoryTab})
          </h4>
          <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0f172a', marginBottom: '0.25rem' }}>
            Dimensions: <span style={{ color: '#2563eb' }}>{reqData.photoSpecs.dimensions}</span>
          </div>
          <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0f172a', marginBottom: '0.75rem' }}>
            Background: <span style={{ color: '#166534' }}>{reqData.photoSpecs.background}</span>
          </div>

          <ul style={{ paddingLeft: '1.2rem', margin: 0, fontSize: '0.825rem', color: '#334155' }}>
            {reqData.photoSpecs.rules.map((rule, i) => (
              <li key={i} style={{ marginBottom: '0.35rem' }}>{rule}</li>
            ))}
          </ul>
        </div>

        {/* Dynamic Document Checklist */}
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '1.25rem' }}>
          <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#14532d', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span>📑</span> Required Documents Checklist ({activeCategoryTab})
          </h4>
          <ul style={{ paddingLeft: '1.2rem', margin: 0, fontSize: '0.825rem', color: '#166534' }}>
            {getCategoryDocuments().map((doc, i) => {
              const cleanDoc = doc.replace(/^\d+[).]\s*/, '');
              return (
                <li key={i} style={{ marginBottom: '0.45rem', fontWeight: '600' }}>
                  {cleanDoc}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* 3. ENTRY & ELIGIBILITY RULES */}
      <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.75rem' }}>
        <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#d48806', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span>⚠️</span> Important Entry Rules ({activeCategoryTab})
        </h4>
        <ul style={{ paddingLeft: '1.2rem', margin: 0, fontSize: '0.825rem', color: '#8c6b00' }}>
          {getCategoryRules().map((req, i) => (
            <li key={i} style={{ marginBottom: '0.35rem' }}>{req}</li>
          ))}
        </ul>
      </div>

      {/* APPROVED BORDER GATES LIST (e.g. VIETNAM) */}
      {reqData.borderGatesNote && (
        <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '12px', padding: '1.25rem', marginBottom: '2rem' }}>
          <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#1e3a8a', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span>🛃</span> Approved Government Entry & Exit Border Gates
          </h4>
          <p style={{ fontSize: '0.85rem', color: '#dc2626', fontWeight: '700', marginBottom: '1rem' }}>
            {reqData.borderGatesNote.title}
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
            {/* Land Gates */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '1rem', borderRadius: '8px' }}>
              <h5 style={{ fontSize: '0.9rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.5rem' }}>
                🏞️ I. List of land border gates ({reqData.borderGatesNote.landGates.length})
              </h5>
              <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.8rem', color: '#334155', paddingRight: '0.25rem' }}>
                {reqData.borderGatesNote.landGates.map((gate, i) => (
                  <div key={i} style={{ marginBottom: '0.25rem' }}>{gate}</div>
                ))}
              </div>
            </div>

            {/* Air Gates */}
            <div style={{ background: '#ffffff', border: '1px solid #bae6fd', padding: '1rem', borderRadius: '8px' }}>
              <h5 style={{ fontSize: '0.9rem', fontWeight: '800', color: '#0369a1', marginBottom: '0.5rem' }}>
                ✈️ II. List of air border gates ({reqData.borderGatesNote.airGates.length})
              </h5>
              <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.8rem', color: '#0284c7', fontWeight: '600', paddingRight: '0.25rem' }}>
                {reqData.borderGatesNote.airGates.map((gate, i) => (
                  <div key={i} style={{ marginBottom: '0.25rem', background: '#f0f9ff', padding: '0.25rem 0.4rem', borderRadius: '4px' }}>{gate}</div>
                ))}
              </div>
            </div>

            {/* Sea Gates */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '1rem', borderRadius: '8px' }}>
              <h5 style={{ fontSize: '0.9rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.5rem' }}>
                🚢 III. List of sea border gates ({reqData.borderGatesNote.seaGates.length})
              </h5>
              <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.8rem', color: '#334155', paddingRight: '0.25rem' }}>
                {reqData.borderGatesNote.seaGates.map((gate, i) => (
                  <div key={i} style={{ marginBottom: '0.25rem' }}>{gate}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Button */}
      <div style={{ textAlign: 'center' }}>
        <button
          type="button"
          onClick={onStartApplication}
          className="btn-primary"
          style={{
            padding: '1rem 2.5rem',
            fontSize: '1.1rem',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
            boxShadow: '0 6px 20px rgba(16, 185, 129, 0.3)',
          }}
        >
          🚀 Start {reqData.countryName} e-Visa Application →
        </button>
      </div>

      {/* DISCLAIMER & NOTICE BOX (AT THE VERY BOTTOM) */}
      <div
        style={{
          background: '#fffbebfb',
          border: '1px solid #fde68a',
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          marginTop: '2rem',
          color: '#92400e',
          fontSize: '0.85rem',
          lineHeight: '1.5',
          boxShadow: '0 2px 8px rgba(245, 158, 11, 0.08)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '800', fontSize: '0.9rem', color: '#b45309', marginBottom: '0.35rem' }}>
          <span>⚠️ IMPORTANT NOTICE & DISCLAIMER</span>
        </div>
        <p style={{ margin: 0 }}>
          This application helper is created by <strong>Madushka Athapaththu</strong> for <strong>mySky Travels</strong> to gather relevant passenger details accurately in order to complete filings on official government visa portals. Misinformation or inaccurate data will result in your application being rejected by immigration authorities, and all associated risks must be borne by the passengers/applicants. <strong>All visa application fees are strictly non-refundable once applied.</strong>
        </p>
      </div>
    </div>
  );
};
